import React, { useEffect, useRef, useState, useCallback } from 'react';
import { View, Text, Pressable, StyleSheet } from 'react-native';
import { useTranslation } from 'react-i18next';
import { PaprikaShadows, Paprika } from '../../styles/theme';

export interface PantryToastData {
  msg: string;
  undo?: () => void;
}

export function usePantryToast() {
  const [toast, setToast] = useState<PantryToastData | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const clear = useCallback(() => {
    if (timer.current) clearTimeout(timer.current);
    setToast(null);
  }, []);
  const flash = useCallback(
    (msg: string, undo?: () => void) => {
      if (timer.current) clearTimeout(timer.current);
      setToast({ msg, undo });
      timer.current = setTimeout(() => setToast(null), undo ? 4200 : 2400);
    },
    []
  );
  useEffect(() => () => {
    if (timer.current) clearTimeout(timer.current);
  }, []);
  return { toast, flash, clear };
}

export function PantryToast({
  toast,
  onUndo,
  desktop = false,
}: {
  toast: PantryToastData;
  onUndo: () => void;
  desktop?: boolean;
}) {
  const { t } = useTranslation();
  const barStyle = desktop ? styles.barDesktop : styles.bar;
  return (
    <View style={barStyle} pointerEvents="box-none">
      <Text style={styles.msg} numberOfLines={2}>
        {toast.msg}
      </Text>
      {toast.undo && (
        <Pressable onPress={onUndo} hitSlop={8}>
          <Text style={styles.undo}>{t('pantry.item.undo')}</Text>
        </Pressable>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  bar: {
    position: 'absolute',
    left: 16,
    right: 16,
    bottom: 78,
    zIndex: 80,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    backgroundColor: Paprika.toastBg,
    borderRadius: 13,
    paddingVertical: 12,
    paddingHorizontal: 14,
    ...PaprikaShadows.modal,
  },
  barDesktop: {
    position: 'absolute',
    left: '50%',
    bottom: 26,
    transform: [{ translateX: '-50%' }],
    zIndex: 80,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    backgroundColor: Paprika.toastBg,
    borderRadius: 13,
    paddingVertical: 12,
    paddingHorizontal: 14,
    ...PaprikaShadows.modal,
  },
  msg: {
    flex: 1,
    fontFamily: 'Inter-Medium',
    fontSize: 13,
    color: Paprika.toastText,
  },
  undo: {
    fontFamily: 'Inter-SemiBold',
    fontSize: 13,
    color: Paprika.toastAction,
  },
});
